import { FunctionComponent } from 'preact';
import { useEffect, useRef } from 'preact/hooks';

export type VideoCallState = 'idle' | 'requesting' | 'connecting' | 'active' | 'ended';

interface VideoCallProps {
  state: VideoCallState;
  localStream: MediaStream | null;
  remoteStream: MediaStream | null;
  cameraOn: boolean;
  micOn: boolean;
  remoteCameraOn: boolean;
  remoteMicOn: boolean;
  onToggleCamera: () => void;
  onToggleMic: () => void;
  onHangup: () => void;
  themeColor: string;
}

const VideoCall: FunctionComponent<VideoCallProps> = ({
  state,
  localStream,
  remoteStream,
  cameraOn,
  micOn,
  remoteCameraOn,
  remoteMicOn,
  onToggleCamera,
  onToggleMic,
  onHangup,
  themeColor,
}) => {
  const localVideoRef = useRef<HTMLVideoElement>(null);
  const remoteVideoRef = useRef<HTMLVideoElement>(null);

  useEffect(() => {
    if (localVideoRef.current && localStream) {
      localVideoRef.current.srcObject = localStream;
    }
  }, [localStream, cameraOn]);

  useEffect(() => {
    if (remoteVideoRef.current && remoteStream) {
      remoteVideoRef.current.srcObject = remoteStream;
    }
  }, [remoteStream, remoteCameraOn]);

  if (state === 'idle' || state === 'ended') return null;

  // Requesting/connecting overlay
  if (state === 'requesting' || state === 'connecting') {
    return (
      <div style={{
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'rgba(0,0,0,0.85)',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 100,
        color: '#fff',
      }}>
        <div style={{
          width: 60,
          height: 60,
          borderRadius: '50%',
          border: '3px solid rgba(255,255,255,0.3)',
          borderTopColor: themeColor,
          animation: 'videocall-spin 1s linear infinite',
          marginBottom: 16,
        }} />
        <p style={{ fontSize: 16, fontWeight: 600, marginBottom: 8 }}>
          {state === 'requesting' ? 'オペレーターを呼び出しています...' : '接続中...'}
        </p>
        <p style={{ fontSize: 13, color: 'rgba(255,255,255,0.6)' }}>
          {state === 'requesting' ? 'しばらくお待ちください' : 'ビデオ通話を準備しています'}
        </p>
        <button
          onClick={onHangup}
          style={{
            marginTop: 24,
            background: '#ef4444',
            color: '#fff',
            border: 'none',
            borderRadius: 28,
            padding: '10px 28px',
            fontSize: 14,
            fontWeight: 600,
            cursor: 'pointer',
          }}
        >
          キャンセル
        </button>
        <style>{`
          @keyframes videocall-spin {
            to { transform: rotate(360deg); }
          }
        `}</style>
      </div>
    );
  }

  // Active call UI
  return (
    <div style={{
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: '#111',
      zIndex: 100,
      display: 'flex',
      flexDirection: 'column',
    }}>
      {/* Remote video (full area) */}
      <div style={{ flex: 1, position: 'relative', overflow: 'hidden' }}>
        {remoteCameraOn && remoteStream ? (
          <video
            ref={remoteVideoRef}
            autoPlay
            playsInline
            style={{
              width: '100%',
              height: '100%',
              objectFit: 'cover',
            }}
          />
        ) : (
          <div style={{
            width: '100%',
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            background: '#1f2937',
          }}>
            <div style={{
              width: 72,
              height: 72,
              borderRadius: '50%',
              background: '#374151',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: 32,
              marginBottom: 8,
            }}>
              👨‍💼
            </div>
            <p style={{ color: '#9ca3af', fontSize: 13 }}>
              {!remoteCameraOn ? 'カメラオフ' : '接続中...'}
            </p>
          </div>
        )}

        {/* Remote audio (always active even if camera is off) */}
        {!remoteCameraOn && remoteStream && (
          <video
            ref={remoteVideoRef}
            autoPlay
            playsInline
            style={{ display: 'none' }}
          />
        )}

        {/* Remote mic muted indicator */}
        {!remoteMicOn && (
          <div style={{
            position: 'absolute',
            top: 12,
            left: 12,
            background: 'rgba(0,0,0,0.6)',
            borderRadius: 16,
            padding: '4px 10px',
            color: '#ef4444',
            fontSize: 11,
            fontWeight: 600,
          }}>
            🔇 ミュート中
          </div>
        )}

        {/* Local video (PiP) */}
        <div style={{
          position: 'absolute',
          bottom: 12,
          right: 12,
          width: 110,
          height: 150,
          borderRadius: 12,
          overflow: 'hidden',
          border: '2px solid rgba(255,255,255,0.3)',
          background: '#1f2937',
        }}>
          {cameraOn && localStream ? (
            <video
              ref={localVideoRef}
              autoPlay
              playsInline
              muted
              style={{
                width: '100%',
                height: '100%',
                objectFit: 'cover',
                transform: 'scaleX(-1)',
              }}
            />
          ) : (
            <div style={{
              width: '100%',
              height: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: '#9ca3af',
              fontSize: 11,
            }}>
              カメラオフ
            </div>
          )}
        </div>
      </div>

      {/* Controls */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 16,
        padding: '16px 0',
        background: '#111',
      }}>
        {/* Camera toggle */}
        <button
          onClick={onToggleCamera}
          style={{
            width: 48,
            height: 48,
            borderRadius: '50%',
            border: 'none',
            background: cameraOn ? '#374151' : '#ef4444',
            color: '#fff',
            fontSize: 20,
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
          title={cameraOn ? 'カメラオフ' : 'カメラオン'}
        >
          {cameraOn ? '📷' : '🚫'}
        </button>

        {/* Mic toggle */}
        <button
          onClick={onToggleMic}
          style={{
            width: 48,
            height: 48,
            borderRadius: '50%',
            border: 'none',
            background: micOn ? '#374151' : '#ef4444',
            color: '#fff',
            fontSize: 20,
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
          title={micOn ? 'ミュート' : 'ミュート解除'}
        >
          {micOn ? '🎤' : '🔇'}
        </button>

        {/* Hang up */}
        <button
          onClick={onHangup}
          style={{
            width: 56,
            height: 56,
            borderRadius: '50%',
            border: 'none',
            background: '#ef4444',
            color: '#fff',
            fontSize: 22,
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
          title="通話終了"
        >
          📞
        </button>
      </div>
    </div>
  );
};

export default VideoCall;
